import React, { useState } from 'react';
import { Container, TextField, Button, Typography, Box, Checkbox, FormControlLabel, CircularProgress, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function Login({ onLogin }) {
  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 402) {
        const data = await response.json();
        console.log("Payment Required response data:", data);
        if (data.payment_link) {
          setMessage(data.error || 'Payment required. Redirecting...');
          window.location.href = data.payment_link;
          return;
        } else {
          setMessage(data.error || 'Payment is required, but payment link is unavailable. Please contact support.');
        }
      } else if (response.ok) {
        const data = await response.json();
        setMessage(data.message || 'Login successful!');
        localStorage.setItem('userToken', data.user_id);
        localStorage.setItem('username', data.user_id);
        localStorage.setItem('company_name', data.company_name);
        if (formData.rememberMe) {
           console.warn("Storing password in localStorage due to 'Remember Me' is insecure.");
        } else {
           localStorage.removeItem('password');
        }

        onLogin(data.user_id);
        navigate('/home');
      } else {
        let errorMsg = `Login failed with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch (jsonError) {
           console.error("Could not parse error response JSON:", jsonError);
        }
        setMessage(errorMsg);
      }
    } catch (error) {
      console.error('Login fetch error:', error);
      setMessage('An error occurred during login. Please check your network connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(to right, #232526, #414345)'
          : 'linear-gradient(to right, #003973, #E5E5BE)',
        margin: 0,
        padding: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          bgcolor: theme.palette.mode === 'dark' ? '#232526' : 'white',
          color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
          boxShadow: 3,
          borderRadius: 2,
          overflow: 'hidden',
          margin: { xs: 2, sm: 4 },
          ml: { xs: 2, sm: 4, md: '20%' },
          mr: { xs: 2, sm: 4, md: '10%' },
          width: { md: '80%' },
        }}
      >
        {/* Left Section with Image */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'block' },
            backgroundImage: 'url("/path-to-your-image.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></Box>

        {/* Right Section with Login Form */}
        <Box
          sx={{
            flex: 1,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
          }}
        >
          <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
            Login to <span style={{ color: '#0056b3' }}>DEALCOST</span>
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="normal"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              fullWidth
              margin="normal"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              }
              label="Remember me"
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              sx={{ mt: 2, position: 'relative' }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: 'white', position: 'absolute' }} /> : 'Login'}
            </Button>
            {message && (
              <Typography
                 color={message.toLowerCase().includes('failed') || message.toLowerCase().includes('error') || message.includes('unavailable') ? 'error' : 'info'}
                 mt={2}
                 align="center"
              >
                {message}
              </Typography>
            )}
          </form>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Typography variant="body2" color="textSecondary" sx={{ cursor: 'pointer' }}>
              Forgot Password
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ cursor: 'pointer' }}>
              Forgot Username
            </Typography>
          </Box>
        </Box>
      </Container>
    </motion.div>
  );
}

export default Login;